/* Chấm công, tính lương, bảo hiểm và thuế TNCN lũy tiến. Port nguyên văn từ app cũ. */

/** [mã, tên, công hưởng lương, tính là ngày làm thực tế] */
export const TS_CODES: [string, string, number, number][] = [
  ['X', 'Đi làm', 1, 1], ['X/2', 'Nửa ngày', 0.5, 0.5], ['CT', 'Công tác', 1, 1], ['P', 'Nghỉ phép', 1, 0], ['L', 'Nghỉ lễ', 1, 0],
  ['GL', 'Nghỉ giảm lương', 1, 0], ['O', 'Ốm (BHXH trả)', 0, 0], ['TS', 'Thai sản', 0, 0], ['KL', 'Không lương', 0, 0],
];
export const TSC: Record<string, [string, string, number, number]> = Object.fromEntries(TS_CODES.map(c => [c[0], c]));

/** Phụ cấp: [khóa, tên, mặc định chịu thuế]. */
export const ALLOW: [string, string, number][] = [
  ['lunch', 'Ăn trưa', 0], ['fuel', 'Xăng xe', 1], ['phone', 'Điện thoại', 0], ['stationery', 'Văn phòng phẩm', 0],
  ['travel', 'Công tác phí', 0], ['perf', 'Thưởng hiệu suất công việc', 1], ['resp', 'Phụ cấp trách nhiệm', 1],
];

export interface PayParams {
  tx_lunch: number; tx_fuel: number; tx_phone: number; tx_stationery: number; tx_travel: number; tx_perf: number; tx_resp: number;
  glRate: number; pers: number; dep: number;
  eSI: number; eHI: number; eUI: number; cSI: number; cHI: number; cUI: number; cTU: number;
  capSI: number; capUI: number;
  [k: string]: number;
}

export const PAY0: PayParams = {
  tx_lunch: 0, tx_fuel: 1, tx_phone: 0, tx_stationery: 0, tx_travel: 0, tx_perf: 1, tx_resp: 1,
  glRate: 100, pers: 15500000, dep: 6200000,
  eSI: 8, eHI: 1.5, eUI: 1, cSI: 17.5, cHI: 3, cUI: 1, cTU: 2,
  capSI: 50600000, capUI: 106200000,
};

export const payParams = (override?: Record<string, number> | null): PayParams => ({ ...PAY0, ...(override || {}) });

/** Biểu thuế TNCN lũy tiến 5 bậc (thu nhập tính thuế/tháng). */
export const PIT_BR: [number, number][] = [[10e6, 0.05], [30e6, 0.1], [60e6, 0.2], [100e6, 0.3], [Infinity, 0.35]];

export function pit(tni: number): number {
  let tax = 0, prev = 0;
  for (const [cap, r] of PIT_BR) {
    if (tni <= prev) break;
    tax += (Math.min(tni, cap) - prev) * r;
    prev = cap;
  }
  return Math.round(tax);
}

/* ---- lịch tháng ---- */
export const daysIn = (m: string): number => new Date(+m.slice(0, 4), +m.slice(5, 7), 0).getDate();
export const dowOf = (m: string, d: number): number => new Date(+m.slice(0, 4), +m.slice(5, 7) - 1, d).getDay();
export const DOW = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

/** Công chuẩn của tháng: bỏ Chủ nhật, và thứ 7 nếu không làm thứ 7. */
export function stdDays(m: string, sat: boolean): number {
  let n = 0;
  for (let d = 1; d <= daysIn(m); d++) { const w = dowOf(m, d); if (w !== 0 && (sat || w !== 6)) n++; }
  return n;
}

export interface EmployeeLite {
  id: string;
  code?: string;
  fullName?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  baseSalary?: number | string;
  insSalary?: number | string;
  dependents?: number | string;
  allowTax?: number | string;
  allowFree?: number | string;
  [k: string]: unknown;
}

/** Nhân sự có mặt trong tháng m (đã vào làm và chưa nghỉ trước đầu tháng). */
export function empInMonth(e: Pick<EmployeeLite, 'startDate' | 'endDate'>, m: string): boolean {
  const s = m + '-01', en = m + '-' + String(daysIn(m)).padStart(2, '0');
  return (e.startDate || '0000') <= en && (!e.endDate || e.endDate >= s);
}

export interface TimesheetSum {
  work: number;
  leave: number;
  paid: number;
  n: Record<string, number>;
}

/**
 * Công thực tế = X + X/2 + CT; Ngày công hưởng lương = P + L + GL;
 * Tổng công tính lương = công thực tế + P + L + GL × tỷ lệ hưởng.
 */
export function tsSum(codes: (string | null | undefined)[] | undefined, glRate: number = PAY0.glRate): TimesheetSum {
  const n: Record<string, number> = {};
  let work = 0;
  for (const c of codes || []) {
    if (!c || !TSC[c]) continue;
    work += TSC[c][3];
    n[c] = (n[c] || 0) + (c === 'X/2' ? 0.5 : 1);
  }
  const leave = (n.P || 0) + (n.L || 0) + (n.GL || 0), gl = (Number(glRate) || 0) / 100;
  return { work, leave, paid: Math.round((work + (n.P || 0) + (n.L || 0) + (n.GL || 0) * gl) * 100) / 100, n };
}

/** Phụ cấp chịu thuế / không chịu thuế. Hồ sơ cũ chỉ có hai ô tổng allowTax/allowFree. */
export function allowSplit(e: EmployeeLite, p: PayParams = PAY0): { at: number; af: number } {
  const has = ALLOW.some(([k]) => e['a_' + k] !== undefined && e['a_' + k] !== '');
  if (!has) return { at: Number(e.allowTax) || 0, af: Number(e.allowFree) || 0 };
  let at = 0, af = 0;
  for (const [k] of ALLOW) {
    const v = Number(e['a_' + k]) || 0;
    if (Number(p['tx_' + k])) at += v; else af += v;
  }
  return { at, af };
}

export interface Timesheet {
  month?: string;
  sat?: boolean;
  std?: number;
  cells?: Record<string, (string | null)[]>;
}

export interface PayInput {
  bonus?: number | string;
  adv?: number | string;
}

export interface PayResult {
  std: number; paid: number; work: number; leave: number;
  base: number; at: number; af: number;
  earned: number; bonus: number; gross: number;
  eSI: number; eHI: number; eUI: number;
  ded: number; tni: number; tax: number; adv: number; net: number;
  cIns: number; noIns: boolean;
}

export function calcPay(e: EmployeeLite, m: string, ts: Timesheet | undefined, inp: PayInput | undefined, p: PayParams = PAY0): PayResult {
  const codes = ts?.cells?.[e.id] || [];
  const s = tsSum(codes, p.glRate);
  const std = Number(ts?.std) || stdDays(m, !!ts?.sat);
  const unpaid = (s.n.O || 0) + (s.n.TS || 0) + (s.n.KL || 0);
  const i = inp || {};
  const base = Number(e.baseSalary) || 0;
  const { at, af } = allowSplit(e, p);
  const r = std ? s.paid / std : 0;
  const earned = Math.round((base + at) * r), free = Math.round(af * r);
  const bonus = Number(i.bonus) || 0, adv = Number(i.adv) || 0;
  const gross = earned + free + bonus;
  // Nghỉ không hưởng lương (ốm, thai sản, không lương) từ 14 ngày trở lên: không đóng BH trong tháng.
  const noIns = unpaid >= 14 || s.paid === 0;
  const ib = Number(e.insSalary) || base;
  const bSI = Math.min(ib, p.capSI), bUI = Math.min(ib, p.capUI);
  const eSI = noIns ? 0 : Math.round((bSI * p.eSI) / 100);
  const eHI = noIns ? 0 : Math.round((bSI * p.eHI) / 100);
  const eUI = noIns ? 0 : Math.round((bUI * p.eUI) / 100);
  const cSI = noIns ? 0 : Math.round((bSI * p.cSI) / 100);
  const cHI = noIns ? 0 : Math.round((bSI * p.cHI) / 100);
  const cUI = noIns ? 0 : Math.round((bUI * p.cUI) / 100);
  const cTU = noIns ? 0 : Math.round((bSI * p.cTU) / 100);
  const ded = p.pers + p.dep * (Number(e.dependents) || 0);
  const tni = Math.max(0, earned + bonus - eSI - eHI - eUI - ded);
  const tax = pit(tni);
  return {
    std, paid: s.paid, work: s.work, leave: s.leave, base, at, af,
    earned: earned + free, bonus, gross, eSI, eHI, eUI, ded, tni, tax, adv,
    net: gross - eSI - eHI - eUI - tax - adv,
    cIns: cSI + cHI + cUI + cTU, noIns,
  };
}
